import cx from 'classnames';
import { type CSSProperties, type MouseEvent } from 'react';
import { Tooltip } from 'react-tooltip';
import FloorPlanTable from './FloorPlanTable';
import styles from './InteractiveFloorPlanTable.scss';
import { calculateIconStyle } from './utils';

const SELECTABLE_OPACITY = 1;
const UNSELECTABLE_OPACITY = 0.3;

export interface TableIconCommonProps {
  iconName: string;
  iconWidthScale: string;
  left: string;
  name: string;
  orientation: string;
  tableIconScale: number;
  testId?: string;
  top: string;
}

export interface InteractiveFloorPlanTableProps extends TableIconCommonProps {
  errorState?: boolean;
  handleTableOnClick?: React.MouseEventHandler<HTMLButtonElement>;
  handleTableOnMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  handleTableOnMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  isComboHovered?: boolean;
  isHighlighted?: boolean;
  isSelectable?: boolean;
  isUnused?: boolean;
  pulse?: boolean;
  showName?: boolean;
  /** The word used in the button's accessible label; defaults to "table". */
  unitLabel?: string;
  unselectableTooltipText?: string;
  highlightIcon?: boolean;
}

export const InteractiveFloorPlanTable = ({
  errorState = false,
  handleTableOnClick,
  handleTableOnMouseEnter,
  handleTableOnMouseLeave,
  iconName,
  iconWidthScale,
  isComboHovered = false,
  isHighlighted,
  isSelectable,
  isUnused,
  left,
  name,
  orientation,
  pulse = false,
  showName = false,
  tableIconScale,
  testId,
  top,
  unitLabel = 'table',
  unselectableTooltipText,
  highlightIcon = true,
}: InteractiveFloorPlanTableProps) => {
  const buttonStyles = cx({
    [styles.button]: true,
    [styles.clickable]: handleTableOnClick && isSelectable,
    [styles.pulse]: pulse,
    [styles.unselectableButton]: !isSelectable,
  });

  const handleOnClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (handleTableOnClick) {
      handleTableOnClick(event);
    }
  };
  let buttonLabel = `Unavailable ${unitLabel}`;
  if (isHighlighted) {
    buttonLabel = `Unselect ${unitLabel}`;
  }
  if (!isHighlighted && isSelectable) {
    buttonLabel = `Select ${unitLabel}`;
  }

  const iconStyle = calculateIconStyle({
    iconWidthScale,
    orientation,
    tableIconScale,
  });
  const iconHighlightCircleStyle = iconStyle.width
    ? ({
        '--iconWidth': `${Number(iconStyle.width) * 1.15}px`,
      } as CSSProperties)
    : {};

  const unusedIndicatorRadius =
    iconStyle.width && Number(iconStyle.width) > 20 ? '5px' : '2px';

  const unusedIndicatorStyle = { r: unusedIndicatorRadius } as CSSProperties;

  return (
    <FloorPlanTable
      data-testid={testId}
      left={left}
      tableIconScale={tableIconScale}
      top={top}
    >
      <button
        aria-label={buttonLabel}
        className={buttonStyles}
        data-tooltip-id={name}
        name={name}
        onClick={handleOnClick}
        onMouseEnter={handleTableOnMouseEnter}
        onMouseLeave={handleTableOnMouseLeave}
        style={highlightIcon ? iconHighlightCircleStyle : undefined}
        tabIndex={isSelectable ? 0 : -1}
        title={showName ? name : undefined}
        type="button"
      >
        <FloorPlanTable.Icon
          hasError={errorState}
          iconName={iconName}
          iconWidthScale={iconWidthScale}
          isComboHovered={isComboHovered}
          isHighlighted={isHighlighted}
          orientation={orientation}
          style={{
            opacity: isSelectable ? SELECTABLE_OPACITY : UNSELECTABLE_OPACITY,
          }}
          tableIconScale={tableIconScale}
        />
        {isUnused && (
          <svg className={styles.unusedSvg}>
            <circle
              className={styles.unusedIndicator}
              style={unusedIndicatorStyle}
            />
          </svg>
        )}
        {showName && (
          <FloorPlanTable.TableName
            tableName={name}
            isDisabled={!isSelectable}
          />
        )}
        {!isSelectable && unselectableTooltipText && (
          <Tooltip
            className={styles.unselectableToolTip}
            content={unselectableTooltipText}
            id={name}
            openOnClick
          />
        )}
      </button>
    </FloorPlanTable>
  );
};
